import React from 'react';
import { QuizQuestion, QuizStats, UserAnswer } from '../types/quiz';
import { formatTime, getScoreMessage } from '../utils/helpers';

interface ResultsProps {
  stats: QuizStats;
  answers: UserAnswer[];
  questions: QuizQuestion[];
  onRestart: () => void;
}

export const Results: React.FC<ResultsProps> = ({ stats, answers, questions, onRestart }) => {
  const scoreMessage = getScoreMessage(stats.scorePercentage);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-4xl w-full">
        <div className="card animate-bounce-in">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4 animate-bounce-in">{scoreMessage.emoji}</div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: 'var(--wcag-text)' }}>
              {scoreMessage.title}
            </h1>
            <p className="text-lg mb-6" style={{ color: 'var(--wcag-text)' }}>
              {scoreMessage.message}
            </p>

            <div className="bg-gradient-to-r from-primary-600 to-accent-600 text-white rounded-2xl p-8 mb-6 shadow-xl">
              <div className="text-6xl font-bold mb-2">
                {stats.correctAnswers}/{stats.totalQuestions}
              </div>
              <div className="text-xl opacity-95 font-semibold">
                Score : {Math.round(stats.scorePercentage)}%
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div
                className="p-4 rounded-lg border-2"
                style={{
                  backgroundColor: 'var(--wcag-success-bg)',
                  borderColor: 'var(--wcag-success-border)',
                }}
              >
                <div className="text-3xl font-bold" style={{ color: 'var(--wcag-success-text)' }}>
                  {stats.correctAnswers}
                </div>
                <div className="text-sm font-medium" style={{ color: 'var(--wcag-success-text)' }}>
                  Bonnes réponses
                </div>
              </div>

              <div
                className="p-4 rounded-lg border-2"
                style={{
                  backgroundColor: 'var(--wcag-danger-bg)',
                  borderColor: 'var(--wcag-danger-border)',
                }}
              >
                <div className="text-3xl font-bold" style={{ color: 'var(--wcag-danger-text)' }}>
                  {stats.wrongAnswers}
                </div>
                <div className="text-sm font-medium" style={{ color: 'var(--wcag-danger-text)' }}>
                  Erreurs
                </div>
              </div>

              <div
                className="p-4 rounded-lg border-2"
                style={{
                  backgroundColor: 'var(--wcag-info-bg)',
                  borderColor: 'var(--wcag-info-border)',
                }}
              >
                <div className="text-3xl font-bold" style={{ color: 'var(--wcag-info-text)' }}>
                  {Math.round(stats.accuracy)}%
                </div>
                <div className="text-sm font-medium" style={{ color: 'var(--wcag-info-text)' }}>
                  Précision
                </div>
              </div>

              <div
                className="p-4 rounded-lg border-2"
                style={{
                  backgroundColor: 'var(--wcag-info-bg)',
                  borderColor: 'var(--wcag-info-border)',
                }}
              >
                <div className="text-3xl font-bold" style={{ color: 'var(--wcag-info-text)' }}>
                  {formatTime(stats.totalTime)}
                </div>
                <div className="text-sm font-medium" style={{ color: 'var(--wcag-info-text)' }}>
                  Temps total
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--wcag-text)' }}>
              📊 Détail de vos réponses
            </h2>
            <div className="space-y-3">
              {answers.map((answer, index) => {
                const question = questions.find(q => q.id === answer.questionId);
                if (!question) return null;

                return (
                  <div
                    key={answer.questionId}
                    className={`p-4 rounded-lg border-2 ${
                      answer.isCorrect
                        ? 'bg-success-50 dark:bg-success-950 border-success-400 dark:border-success-600'
                        : 'bg-danger-50 dark:bg-danger-950 border-danger-400 dark:border-danger-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{answer.isCorrect ? '✅' : '❌'}</span>
                      <div className="flex-1">
                        <div className="font-semibold mb-1" style={{ color: 'var(--wcag-text)' }}>
                          Question {index + 1} : {question.question}
                        </div>
                        <div className="text-sm" style={{ color: 'var(--wcag-text)' }}>
                          Temps : {formatTime(answer.timeSpent)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-950 dark:to-accent-950 p-6 rounded-lg border-2 border-primary-300 dark:border-primary-700 mb-6">
            <h3
              className="font-bold mb-3 flex items-center gap-2"
              style={{ color: 'var(--wcag-text)' }}
            >
              <span>🎯</span>
              Points clés à retenir
            </h3>
            <ul className="space-y-2" style={{ color: 'var(--wcag-text)' }}>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: 'var(--wcag-primary)' }}>
                  •
                </span>
                <span>Ne partagez JAMAIS vos codes confidentiels par téléphone ou email</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: 'var(--wcag-primary)' }}>
                  •
                </span>
                <span>Vérifiez toujours l'URL des sites web avant de saisir des informations</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: 'var(--wcag-primary)' }}>
                  •
                </span>
                <span>
                  En cas de doute, contactez directement l'organisme via leurs canaux officiels
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: 'var(--wcag-primary)' }}>
                  •
                </span>
                <span>Si c'est trop beau pour être vrai, c'est probablement une arnaque</span>
              </li>
            </ul>
          </div>

          <button onClick={onRestart} className="btn-primary w-full text-lg py-4">
            🔄 Recommencer le quiz
          </button>

          <div className="mt-6 text-center text-sm" style={{ color: 'var(--wcag-text-muted)' }}>
            <p>💡 Partagez ce quiz avec vos proches pour les aider à se protéger !</p>
          </div>
        </div>
      </div>
    </div>
  );
};
